import React from "react";
import { array } from "fp-ts";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMRoadblockTableSheet } from "../../components/CRMRoadblock/CRMRoadblockTableSheet/CRMRoadblockTableSheet";
import { CRMJobsRoadblocksTableFilter } from "../../components/CRMJobs/CRMJobsRoadblocksTableFilter/CRMJobsRoadblocksTableFilter";
import { CRMDayWeekStrip } from "../../components/CRM/CRMCases/CRMDayWeekStrip/CRMDayWeekStrip";
import { CRMDayRoadblockFilter } from "../../components/CRM/CRMCases/CRMDayRoadblockTable/CRMDayRoadblockFilter";
import { getListingRoadblockChecklistItemOptions, listingRoadblockTypeOptions } from "../../../../domain/listingRoadblocks/listingRoadblockChecklistDisplay";
import { CRMJobsFilterPlaceholder } from "../../components/CRMJobs/CRMJobsFilterPlaceholder/CRMJobsFilterPlaceholder";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { Background } from "../../components/BuildingBlocks/Background";
import { ShineShape } from "../../components/BuildingBlocks/ShineShape";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { TRoadblocksForm } from "../../../../domain/codecs/form/RoadblockForm";
import { TListingRoadblockType, TListingRoadblockChecklistItemType } from "../../../../domain/codecs/form/ListingRoadblockForm";
import { TJobsFilter } from "../../../../domain/codecs/statePartials/JobsPartial";

export const CRMHomesJobsDashboard = (props: TContainerStateProps): JSX.Element => {

    const jobsData = props.state.forms.jobs.data;
    const isLoading =
        (jobsData.status === "loading" || jobsData.status === "submitting")
        && jobsData.output.jobs.length === 0;

    const onChangeRoadblocks = (payload: TRoadblocksForm) => props.dispatch({
        type: "FILTER_ROADBLOCKS",
        payload,
    });

    const onChangeJobs = (payload: TJobsFilter) => props.dispatch({
        type: "FILTER_JOBS",
        payload,
    });

    const onLoadMoreRoadblocks = () => props.dispatch({
        type: "LOAD_MORE_ROADBLOCKS",
        payload: null,
    });

    const listingRoadblocksForm = props.state.forms.overlay.listing_roadblocks;

    const onChangeListingRoadblocksTypeFilter = (
        listingRoadblockType: TListingRoadblockType | null,
        checklistItemType: TListingRoadblockChecklistItemType | null,
    ) =>
        props.dispatch({
            type: "SH_LISTING_ROADBLOCKS_FILTER_CHANGE",
            payload: {
                ...listingRoadblocksForm,
                edited: {
                    ...listingRoadblocksForm.edited,
                    listing_roadblock_type: listingRoadblockType,
                    checklist_item_type: checklistItemType,
                },
            },
        });

    const onPickListingRoadblocksDate = (date: { type: "iso", date: string } | { type: "overdue" }) =>
        props.dispatch({
            type: "SH_LISTING_ROADBLOCKS_FILTER_CHANGE",
            payload: {
                ...listingRoadblocksForm,
                edited: { ...listingRoadblocksForm.edited, date },
            },
        });

    return (
        <Background padding="0px">
            {isLoading &&
                <Placeholder />
            }

            {!isLoading &&
                <SpacingColumn spacing={CRMSpacing.LARGE}>
                    <CRMDayWeekStrip
                        form={props.state.forms.overlay.listing_roadblocks_week_counts}
                        dateFilter={listingRoadblocksForm.edited.date}
                        viewingName={null}
                        onPickDate={(date) => onPickListingRoadblocksDate({ type: "iso", date })}
                        onPickOverdue={() => onPickListingRoadblocksDate({ type: "overdue" })}
                    />

                    <CRMDayRoadblockFilter
                        types={listingRoadblockTypeOptions}
                        getItemOptions={getListingRoadblockChecklistItemOptions}
                        showSourceOptions={false}
                        value={{
                            source: "roadblocks",
                            roadblock_type: listingRoadblocksForm.edited.listing_roadblock_type,
                            checklist_item_type: listingRoadblocksForm.edited.checklist_item_type,
                        }}
                        onChange={(value) => onChangeListingRoadblocksTypeFilter(value.roadblock_type, value.checklist_item_type)}
                    />

                    <CRMJobsRoadblocksTableFilter
                        form={props.state.forms.overlay.roadblocks}
                        jobsFilter={props.state.forms.jobs.filter}
                        jobsForm={jobsData}
                        staffUsers={jobsData.output.all_staff_users}
                        onChangeRoadblocks={onChangeRoadblocks}
                        onChangeJobs={onChangeJobs}
                    />

                    <CRMRoadblockTableSheet
                        roadblocksForm={props.state.forms.overlay.roadblocks}
                        listingRoadblocksForm={props.state.forms.overlay.listing_roadblocks}
                        jobsForm={jobsData}
                        jobsFilter={props.state.forms.jobs.filter}
                        jobsGroupingMode={null}
                        adhocJobsPlacement="above_roadblocks"
                        onLoadMore={onLoadMoreRoadblocks}
                        onAssignListingRoadblock={(payload) => props.dispatch({
                            type: "SH_LISTING_ROADBLOCK_ASSIGN",
                            payload,
                        })}
                        onAwaitListingRoadblock={(payload) => props.dispatch({
                            type: "SH_LISTING_ROADBLOCK_AWAIT",
                            payload,
                        })}
                        onCancelListingRoadblockAwait={(payload) => props.dispatch({
                            type: "SH_LISTING_ROADBLOCK_CANCEL_AWAIT",
                            payload,
                        })}
                        onReassignJob={(job, assignedTo) => props.dispatch({
                            type: "REASSIGN_ADHOC_JOB",
                            payload: { job, assignedTo },
                        })}
                        onDeferJob={(job, deferredDate) => props.dispatch({
                            type: "DEFER_ADHOC_JOB",
                            payload: { job, deferredDate },
                        })}
                        emptyMessage="You're all caught up — no jobs to pick up right now."
                    />
                </SpacingColumn>
            }
        </Background>
    );
};

const Placeholder = (): JSX.Element =>
    <SpacingColumn spacing={CRMSpacing.LARGE}>
        <CRMJobsFilterPlaceholder />

        <SpacingColumn spacing={CRMSpacing.SMALL}>
            {array.range(0, 9).map((number) =>
                <ShineShape
                    key={number}
                    backgroundColor={CRMColors.NEUTRAL_10}
                    shineColor={CRMColors.NEUTRAL_12}
                    duration={2000}
                    height={"24px"}
                    width={"100%"}
                />
            )}
        </SpacingColumn>
    </SpacingColumn>
;
